import { useState, useCallback } from 'react';
import { Play, Loader2, AlertCircle, Trash2, Crosshair } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileUploader } from './file-uploader';
import { ParameterEditor } from './parameter-editor';
import { SimulationResults } from './simulation-results';
import { useCFDLayers } from '@/hooks/use-cfd-layers';
import { useMapInteraction } from '@/contexts/MapInteractionContext';
import { runSimulation } from './lib/simulation-service';
import type { SimulationParams, SimulationResponse, UploadResult } from './types';

const DEFAULT_PARAMS: SimulationParams = {
  manning_n: 0.03,
  infiltration_factor: 0.3,
  rainfall_rate: 50,
  rainfall_units: 'mm/hr',
  duration_minutes: 120,
  dt_seconds: 60,
  min_pond_size_pixels: 5,
  min_pond_depth_meters: 0.01,
};

export interface CFDSimulationPanelProps {
  onSimulationComplete?: (result: SimulationResponse) => void;
  onError?: (error: string) => void;
}

export function CFDSimulationPanel({
  onSimulationComplete,
  onError,
}: CFDSimulationPanelProps) {
  // GCS URL state
  const [demUrl, setDemUrl] = useState<string | null>(null);
  const [buildingsUrl, setBuildingsUrl] = useState<string | null>(null);
  const [hyetographUrl, setHyetographUrl] = useState<string | null>(null);

  // Simulation state
  const [params, setParams] = useState<SimulationParams>(DEFAULT_PARAMS);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<SimulationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // CFD layers hook for map integration
  const { addDEMLayer, addBuildingLayer, addResultLayer, addPondsLayer } = useCFDLayers();
  
  // Map interaction for ROI selection
  const { mode, roiBounds, activateDrawRoiMode, clearRoi, deactivateMode } = useMapInteraction();

  // File upload handlers
  const handleDemUpload = useCallback(async (uploadResult: UploadResult, file: File) => {
    setDemUrl(uploadResult.gcsUrl);
    // Add DEM layer to map
    const result = await addDEMLayer(uploadResult.gcsUrl, file.name);
    if (!result.success) {
      console.error('Failed to add DEM to map:', result.error);
    }
  }, [addDEMLayer]);

  const handleBuildingsUpload = useCallback(async (uploadResult: UploadResult, file: File) => {
    setBuildingsUrl(uploadResult.gcsUrl);
    // Add building layer to map
    const result = await addBuildingLayer(uploadResult.gcsUrl, file.name);
    if (!result.success) {
      console.error('Failed to add building layer to map:', result.error);
    }
  }, [addBuildingLayer]);

  const handleHyetographUpload = useCallback((uploadResult: UploadResult, _file: File) => {
    setHyetographUrl(uploadResult.gcsUrl);
  }, []);

  const handleUploadError = useCallback((errorMsg: string) => {
    setError(errorMsg);
    onError?.(errorMsg);
  }, [onError]);

  // Run simulation
  const handleRunSimulation = async () => {
    if (!demUrl || !buildingsUrl) {
      const errorMsg = 'Please upload DEM and Buildings files';
      setError(errorMsg);
      onError?.(errorMsg);
      return;
    }

    setIsRunning(true);
    setError(null);
    setResult(null);

    try {
      // Run simulation via service
      const data = await runSimulation(
        demUrl,
        buildingsUrl,
        params,
        hyetographUrl || undefined,
        roiBounds
      );
      setResult(data);
      
      // Add result layers to map
      if (data.files?.peak_depth_tif) {
        const result = await addResultLayer(data.files.peak_depth_tif, 'Peak Depth', 'blues_r');
        if (!result.success) {
          console.error('Failed to add peak depth layer to map:', result.error);
        }
      }
      
      if (data.files?.filtered_ponds_tif) {
        const result = await addPondsLayer(data.files.filtered_ponds_tif, 'Filtered Ponds');
        if (!result.success) {
          console.error('Failed to add filtered ponds layer to map:', result.error);
        }
      }
      
      onSimulationComplete?.(data);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setIsRunning(false);
    }
  };

  const canRun = demUrl && buildingsUrl && !isRunning;

  return (
    <div className="space-y-4">
      {/* File Uploads */}
      <div className="space-y-3">
        <div className="text-xs font-medium text-muted-foreground">Input Files</div>
        
        <FileUploader
          label="DEM"
          accept=".tif,.tiff"
          required
          onUploadComplete={handleDemUpload}
          onError={handleUploadError}
          verifyCog
        />

        <FileUploader
          label="Building Mask"
          accept=".tif,.tiff"
          required
          onUploadComplete={handleBuildingsUpload}
          onError={handleUploadError}
          verifyCog
        />

        <FileUploader
          label="Hyetograph (Optional)"
          accept=".csv"
          onUploadComplete={handleHyetographUpload}
          onError={handleUploadError}
        />
      </div>

      {/* ROI Selection */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-muted-foreground">Region of Interest (Optional)</div>
        {!roiBounds ? (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={activateDrawRoiMode}
            disabled={isRunning}
          >
            <Crosshair className="h-4 w-4 mr-2" />
            {mode === 'draw-roi' ? 'Drawing ROI...' : 'Select ROI on Map'}
          </Button>
        ) : (
          <div className="border rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium">Selected Region</div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => {
                  clearRoi();
                  deactivateMode();
                }}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Clear
              </Button>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <div>North: {roiBounds.north.toFixed(6)}</div>
              <div>South: {roiBounds.south.toFixed(6)}</div>
              <div>East: {roiBounds.east.toFixed(6)}</div>
              <div>West: {roiBounds.west.toFixed(6)}</div>
              <div className="pt-1 border-t">
                Extent: {((roiBounds.east - roiBounds.west) * 111.32 * Math.cos(roiBounds.north * Math.PI / 180)).toFixed(2)} × {((roiBounds.north - roiBounds.south) * 111.32).toFixed(2)} km
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Parameters */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-muted-foreground">Simulation Parameters</div>
        <ParameterEditor params={params} onChange={setParams} disabled={isRunning} />
      </div>

      {/* Run Button */}
      <Button
        className="w-full"
        onClick={handleRunSimulation}
        disabled={!canRun}
      >
        {isRunning ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Running Simulation...
          </>
        ) : (
          <>
            <Play className="h-4 w-4 mr-2" />
            Run Simulation
          </>
        )}
      </Button>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">{error}</AlertDescription>
        </Alert>
      )}

      {/* Results */}
      {result && <SimulationResults result={result} />}
    </div>
  );
}
